import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { useForm } from '@inertiajs/react'
import { FormEvent } from 'react'
import { Mail, Info } from 'lucide-react'

interface Community {
  id: number
  name: string
}

interface NewProps {
  communities: Community[]
}

export default function New({ communities }: NewProps) {
  const { data, setData, post, processing, errors } = useForm({
    emails: '',
    community_id: ''
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    post('/admin/invitations')
  }

  const emailCount = data.emails
    .split(/[,;\n]+/)
    .map(e => e.trim())
    .filter(e => e.length > 0)
    .length

  return (
    <AdminLayout
      title="Inviter des utilisateurs"
      breadcrumbs={[
        { label: 'Invitations', href: '/admin/invitations' },
        { label: 'Nouvelle invitation' }
      ]}
    >
      <div className="max-w-2xl">
        <form onSubmit={handleSubmit}>
          <Card className="p-6">
            {/* Info Banner */}
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg mb-6">
              <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Inviter de nouveaux membres</p>
                <p>
                  Les personnes invitées recevront un email avec un lien pour créer leur compte.
                  L'invitation expire après 7 jours.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Emails */}
              <div>
                <label htmlFor="emails" className="block text-sm font-medium text-text-dark mb-1">
                  Adresses email *
                </label>
                <Textarea
                  id="emails"
                  value={data.emails}
                  onChange={(e) => setData('emails', e.target.value)}
                  placeholder="exemple1@email.com&#10;exemple2@email.com&#10;exemple3@email.com"
                  rows={6}
                  required
                />
                {errors.emails && (
                  <p className="mt-1 text-sm text-red-600">{errors.emails}</p>
                )}
                <div className="mt-1 flex items-center justify-between text-xs text-text-gray">
                  <span>Séparez les adresses par des virgules, points-virgules ou retours à la ligne.</span>
                  {emailCount > 0 && (
                    <span className="font-medium text-tessan-primary">
                      {emailCount} email{emailCount > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>

              {/* Community */}
              <div>
                <label htmlFor="community_id" className="block text-sm font-medium text-text-dark mb-1">
                  Communauté *
                </label>
                <Select
                  id="community_id"
                  value={data.community_id}
                  onChange={(e) => setData('community_id', e.target.value)}
                  required
                >
                  <option value="">Sélectionner une communauté</option>
                  {communities.map((community) => (
                    <option key={community.id} value={community.id}>
                      {community.name}
                    </option>
                  ))}
                </Select>
                {errors.community_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.community_id}</p>
                )}
                <p className="mt-1 text-xs text-text-gray">
                  Les utilisateurs invités seront automatiquement associés à cette communauté.
                </p>
              </div>
            </div>

            {/* Submit */}
            <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end gap-4">
              <Button type="button" variant="secondary" onClick={() => history.back()}>
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={processing || emailCount === 0}
                className="flex items-center gap-2"
              >
                <Mail size={18} />
                {processing ? 'Envoi...' : `Envoyer ${emailCount > 0 ? emailCount : ''} invitation${emailCount > 1 ? 's' : ''}`}
              </Button>
            </div>
          </Card>
        </form>

        {/* Help Card */}
        <Card className="p-6 mt-6">
          <h3 className="text-lg font-semibold text-text-dark mb-4">Conseils</h3>
          <ul className="space-y-2 text-sm text-text-gray">
            <li className="flex items-start gap-2">
              <span className="text-tessan-primary">•</span>
              <span>Vérifiez que les adresses email sont correctes avant d'envoyer.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-tessan-primary">•</span>
              <span>Les invitations expirent après 7 jours. Vous pouvez les renvoyer si nécessaire.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-tessan-primary">•</span>
              <span>Les utilisateurs déjà inscrits ou ayant une invitation en cours seront ignorés.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-tessan-primary">•</span>
              <span>Vous pouvez inviter plusieurs personnes en une seule fois.</span>
            </li>
          </ul>
        </Card>
      </div>
    </AdminLayout>
  )
}
